import React, { useEffect, useMemo, useState } from 'react';
import { Marker, Polyline } from 'react-leaflet';
import L from 'leaflet';
import RGBcolor from 'color';
import { CompetitorType, TrackPoint, useEventContext } from 'providers/EventProvider';

const ARROW_SIZE = 22;
const CIRCLE_SIZE = 14;
const ARROW_NAME_GAP_PX = 12;
const CIRCLE_NAME_GAP_PX = 12;

const getBearing = (from: TrackPoint, to: TrackPoint) => {
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const dLng = ((to.lng - from.lng) * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const cssFromStyle = (style: Record<string, string>) =>
  Object.entries(style)
    .map(([key, value]) => `${key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}:${value}`)
    .join(';');

const createHeadIcon = (
  size: number,
  nameGap: number,
  shapeHtml: string,
  shortName: string,
  nameStyle: Record<string, string>,
  highlight: boolean
) =>
  L.divIcon({
    className: 'player-head-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `<div class="player-head-marker">
      ${shapeHtml}
      <span class="player-head-name${highlight ? ' highlight_on' : ''}" style="left:calc(50% + ${nameGap}px);${cssFromStyle(nameStyle)}">${escapeHtml(shortName)}</span>
    </div>`
  });

const createArrowIcon = (
  fillColor: string,
  strokeColor: string,
  bearing: number,
  shortName: string,
  nameStyle: Record<string, string>,
  highlight: boolean
) =>
  createHeadIcon(
    ARROW_SIZE,
    ARROW_NAME_GAP_PX,
    `<svg width="${ARROW_SIZE}" height="${ARROW_SIZE}" viewBox="0 0 24 24" style="transform:rotate(${bearing}deg)">
      <path d="M12 2 L20 18 L12 14.4 L4 18 Z" fill="${fillColor}" stroke="${strokeColor}" stroke-width="1.75" stroke-linejoin="round"/>
    </svg>`,
    shortName,
    nameStyle,
    highlight
  );

const createCircleIcon = (fillColor: string, shortName: string, nameStyle: Record<string, string>, highlight: boolean) =>
  createHeadIcon(
    CIRCLE_SIZE,
    CIRCLE_NAME_GAP_PX,
    `<svg width="${CIRCLE_SIZE}" height="${CIRCLE_SIZE}" viewBox="0 0 14 14">
      <circle cx="7" cy="7" r="5" fill="${fillColor}" stroke="black" stroke-width="2"/>
    </svg>`,
    shortName,
    nameStyle,
    highlight
  );

const calculateStyle = (player: CompetitorType) => {
  const rgb = new RGBcolor(player.color);
  const shadowColor = rgb.red() * 0.299 + rgb.green() * 0.587 + rgb.blue() * 0.114 > 186 ? '#000000' : '#ffffff';
  return {
    color: player.color,
    textShadow: `-1px -1px 0 ${shadowColor}, 1px -1px 0 ${shadowColor}, -1px 1px 0 ${shadowColor}, 1px 1px 0 ${shadowColor}`,
    fontWeight: 'bold'
  };
};

const MarkerPlayer = (props: MarkerPlayerProps) => {
  const { event, options, time } = useEventContext();
  const eventStartDate = React.useMemo(() => new Date(event.startDate).getTime(), [event]);
  const { player } = props;
  const [polyLine, setPolyLine] = useState<TrackPoint[]>([]);
  const [marker, setMarker] = useState<TrackPoint>();
  const [opacity, setOpacity] = useState(0.5);

  const [style, setStyle] = useState(calculateStyle(player));

  const bearing = useMemo(() => {
    if (polyLine.length < 2) return 0;
    const last = polyLine[polyLine.length - 1];
    let fromIdx = polyLine.length - 2;
    while (fromIdx >= 0 && polyLine[fromIdx].lat === last.lat && polyLine[fromIdx].lng === last.lng) {
      fromIdx--;
    }
    if (fromIdx < 0) return 0;
    return getBearing(polyLine[fromIdx], last);
  }, [polyLine]);

  const playerIcon = useMemo(() => {
    const fillColor = player.color;
    const { zIndex: _zIndex, ...nameStyle } = style as Record<string, string>;
    switch (event.markerType) {
      case 'circle':
        return createCircleIcon(fillColor, player.shortName, nameStyle, player.options.highlight);
      case 'arrow': {
        const strokeColor = new RGBcolor(fillColor).mix(new RGBcolor('#000000'), 0.5).hex();
        return createArrowIcon(fillColor, strokeColor, bearing, player.shortName, nameStyle, player.options.highlight);
      }
      default: {
        const _exhaustive: never = event.markerType;
        throw new Error(`Unhandled marker type: ${_exhaustive}`);
      }
    }
  }, [event.markerType, player.color, player.shortName, player.options.highlight, bearing, style]);

  useEffect(() => {
    const polyLine: TrackPoint[] = [];
    const nextTime = time + (options.isMassStart ? player.offsetTime : options.startEventTime);

    if (player.options.show) {
      player.track.forEach((d, i) => {
        if (d.time <= nextTime && (options.tail === 10000 || player.options.fullRoute || d.time >= nextTime - options.tail * 1000)) {
          polyLine.push(d);
        }
      });

      if (polyLine.length > 0) {
        const last = polyLine[polyLine.length - 1];
        const next = player.track.filter((d) => d.time > last.time);
        if (next && next[0]) {
          const start = last;
          const second = next[0];
          const k = (nextTime - start.time) / (second.time - start.time);
          const vLat = (second.lat - start.lat) * k;
          const vLng = (second.lng - start.lng) * k;
          const end = {
            lat: start.lat + vLat,
            lng: start.lng + vLng,
            time: start.time + k
          };
          polyLine.push(end);
        }
      } else {
        const last = player.track.filter((d) => d.time < nextTime);
        if (last && last[last.length - 1]) {
          polyLine.push(last[last.length - 1]);
        }
      }
    }

    setPolyLine(polyLine);
    if (polyLine.length) {
      const newMarker = polyLine[polyLine.length - 1];
      setMarker(newMarker);
      setOpacity(player.isVirtual ? 0.7 : newMarker.time < eventStartDate + time - event.delay * 500 ? 0.5 : 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time, player.isVirtual, player.options.fullRoute, player.options.show, player.offsetTime, options.isMassStart, options.tail]);

  useEffect(() => {
    const newStyle = calculateStyle(player);
    if (player.options.highlight) {
      Object.assign(newStyle, {
        padding: '6px',
        background: 'white',
        border: '1px solid black',
        borderRadius: '5px',
        marginLeft: '-5px',
        zIndex: 'inherit'
      });
    }
    setStyle(newStyle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.color, player.options.highlight]);

  if (polyLine.length === 0) return null;

  return (
    <React.Fragment>
      <Polyline
        positions={polyLine}
        pathOptions={{
          color: player.color,
          opacity: options.routeOpacity,
          weight: options.routeWeight
        }}
      />
      {marker && (
        <Marker position={marker} icon={playerIcon} opacity={opacity} zIndexOffset={player.options.highlight ? 1000 : 0} />
      )}
    </React.Fragment>
  );
};

export default MarkerPlayer;

type MarkerPlayerProps = {
  player: CompetitorType;
};
